import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Plus, Edit3, Trash2, Euro, Wallet, Target } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, BarChart, Bar } from "recharts";

const MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

export default function ChefFinance() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sites, setSites] = useState(null); // null = multi-sites désactivé
  const [scope, setScope] = useState("all"); // all | siege | <site_id>
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  function empty() {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1, ca_ht: "", charges: "", notes: "", site_id: scope !== "all" && scope !== "siege" ? scope : "" };
  }

  const load = async () => {
    try {
      const params = scope === "all" ? {} : { site_id: scope };
      const [a, b] = await Promise.all([
        api.get("/chef/finance", { params }),
        api.get("/chef/finance/summary", { params }),
      ]);
      setItems(a.data);
      setSummary(b.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);
  useEffect(() => { api.get("/chef/sites").then((r) => setSites(r.data || [])).catch(() => setSites(null)); }, []);
  const siteName = (id) => (id ? sites?.find((s) => s.id === id)?.name || "Établissement supprimé" : "Siège / non affecté");

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/chef/finance", {
        year: parseInt(form.year),
        month: parseInt(form.month),
        ca_ht: parseFloat(form.ca_ht) || 0,
        charges: parseFloat(form.charges) || 0,
        notes: form.notes || "",
        site_id: form.site_id || null,
      });
      toast.success(editing ? "CA mis à jour" : "CA enregistré");
      setOpen(false); setEditing(null); setForm(empty()); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ year: item.year, month: item.month, ca_ht: item.ca_ht || "", charges: item.charges || "", notes: item.notes || "", site_id: item.site_id || "" });
    setOpen(true);
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette entrée ?")) return;
    try { await api.delete(`/chef/finance/${id}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const chartData = useMemo(() => {
    if (!summary) return [];
    return summary.months.map((m) => ({
      month: MONTHS[m.month - 1],
      [`CA ${summary.current_year}`]: m.ca_n,
      [`CA ${summary.last_year}`]: m.ca_n1,
      [`Marge ${summary.current_year}`]: +(m.ca_n - m.charges_n).toFixed(2),
    }));
  }, [summary]);

  const evolutionUp = (summary?.evolution_pct || 0) >= 0;

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Finance"
        title="Tableau de bord CA"
        sub="Suivez votre chiffre d'affaires mois par mois et comparez avec l'année précédente."
        right={
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty()); } }}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="finance-add-btn" className="h-11 bg-[#002FA7] text-white"><Plus className="h-4 w-4 mr-2" /> Saisir un mois</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-black tracking-tight">{editing ? "Modifier le mois" : "Saisie CA mensuel"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="finance-form">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Année</Label>
                    <Select value={String(form.year)} onValueChange={(v) => setForm({ ...form, year: v })}>
                      <SelectTrigger data-testid="finance-year" className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[2023, 2024, 2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Mois</Label>
                    <Select value={String(form.month)} onValueChange={(v) => setForm({ ...form, month: v })}>
                      <SelectTrigger data-testid="finance-month" className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m} ({String(i + 1).padStart(2, "0")})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <F label="CA HT (€)" value={form.ca_ht} onChange={(v) => setForm({ ...form, ca_ht: v })} type="number" step="0.01" testid="finance-ca" />
                  <F label="Charges (€)" value={form.charges} onChange={(v) => setForm({ ...form, charges: v })} type="number" step="0.01" testid="finance-charges" />
                </div>
                {sites !== null && (
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Établissement</Label>
                    <Select value={form.site_id || "siege"} onValueChange={(v) => setForm({ ...form, site_id: v === "siege" ? "" : v })}>
                      <SelectTrigger data-testid="finance-site" className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="siege">Siège / non affecté</SelectItem>
                        {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="finance-notes" placeholder="Optionnel — événements particuliers ce mois" />
                </div>
                <DialogFooter><Button type="submit" data-testid="finance-submit" className="bg-[#002FA7] text-white">{editing ? "Mettre à jour" : "Enregistrer"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="p-6 lg:p-10 space-y-6">
        {sites !== null && (
          <div className="flex items-center gap-3 flex-wrap" data-testid="finance-scope-bar">
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#868E96]">Périmètre</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="finance-scope" className="h-10 w-72 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Consolidé — tous les établissements</SelectItem>
                <SelectItem value="siege">Siège / non affecté</SelectItem>
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {/* KPI cards */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-[#0A0A0A]" data-testid="finance-kpis">
            <Kpi label={`CA ${summary.current_year}`} value={summary.ca_n} suffix="€" testid="kpi-ca-n" highlight />
            <Kpi label={`CA ${summary.last_year}`} value={summary.ca_n1} suffix="€" testid="kpi-ca-n1" muted />
            <Kpi label={`Marge ${summary.current_year}`} value={summary.marge_n} suffix="€" testid="kpi-marge" />
            <KpiEvol label="Évolution N vs N-1" value={summary.evolution_pct} up={evolutionUp} undefined={summary.evolution_undefined} testid="kpi-evolution" />
          </div>
        )}

        {/* Chart */}
        {chartData.length > 0 && (
          <div className="bg-white border border-[#DEE2E6] p-5" data-testid="finance-chart-ca">
            <div className="text-xs uppercase tracking-[0.25em] font-bold mb-3">Chiffre d'affaires N vs N-1</div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `${v.toLocaleString("fr-FR")} €`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey={`CA ${summary.current_year}`} stroke="#002FA7" strokeWidth={2.5} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey={`CA ${summary.last_year}`} stroke="#868E96" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {chartData.length > 0 && (
          <div className="bg-white border border-[#DEE2E6] p-5" data-testid="finance-chart-marge">
            <div className="text-xs uppercase tracking-[0.25em] font-bold mb-3">Marge mensuelle (CA - Charges)</div>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `${v.toLocaleString("fr-FR")} €`} />
                  <Bar dataKey={`Marge ${summary.current_year}`} fill="#FFD700" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Liste */}
        <div className="bg-white border border-[#DEE2E6]" data-testid="finance-list">
          <div className="grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-bold border-b border-[#DEE2E6]">
            <div className="col-span-2">Période</div>
            <div className="col-span-3">CA HT</div>
            <div className="col-span-2">Charges</div>
            <div className="col-span-2">Marge</div>
            <div className="col-span-2">Notes</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {items.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#868E96]">
              Aucune entrée. Cliquez sur "Saisir un mois" pour commencer.
            </div>
          ) : items.map((item) => {
            const marge = +(item.ca_ht - item.charges).toFixed(2);
            return (
              <div key={item.id} className="grid grid-cols-12 px-5 py-3 items-center border-b border-[#F8F9FA] last:border-b-0 hover:bg-[#F8F9FA]" data-testid={`finance-row-${item.id}`}>
                <div className="col-span-2 font-mono text-sm font-bold">{MONTHS[item.month - 1]} {item.year}{sites !== null && <div className="text-[10px] font-sans font-semibold text-[#002FA7] truncate" data-testid={`finance-row-site-${item.id}`}>{siteName(item.site_id)}</div>}</div>
                <div className="col-span-3 font-mono text-sm">{item.ca_ht.toLocaleString("fr-FR")} €</div>
                <div className="col-span-2 font-mono text-sm text-[#E60000]">-{item.charges.toLocaleString("fr-FR")} €</div>
                <div className={`col-span-2 font-mono text-sm font-bold ${marge >= 0 ? "text-[#059669]" : "text-[#E60000]"}`}>{marge.toLocaleString("fr-FR")} €</div>
                <div className="col-span-2 text-xs text-[#495057] italic truncate">{item.notes || "—"}</div>
                <div className="col-span-1 flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => openEdit(item)} className="h-7 px-2"><Edit3 className="h-3 w-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(item.id)} className="h-7 px-2 border-[#E60000] text-[#E60000]"><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, suffix, testid, highlight, muted }) {
  return (
    <div data-testid={testid} className={`p-5 lg:p-6 border-r border-[#0A0A0A] last:border-r-0 ${highlight ? "bg-[#002FA7] text-white" : muted ? "bg-[#F8F9FA]" : "bg-white"}`}>
      <div className="flex items-center gap-2">
        <Wallet className={`h-3 w-3 ${highlight ? "text-[#FFD700]" : "text-[#002FA7]"}`} />
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold opacity-70">{label}</div>
      </div>
      <div className="font-display text-2xl lg:text-3xl font-black tracking-tighter mt-1.5">
        {(value || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-base font-bold">{suffix}</span>
      </div>
    </div>
  );
}

function KpiEvol({ label, value, up, undefined: isUndefined, testid }) {
  const Icon = up ? TrendingUp : TrendingDown;
  if (isUndefined) {
    return (
      <div data-testid={testid} className="p-5 lg:p-6 bg-[#F8F9FA]">
        <div className="flex items-center gap-2">
          <Target className="h-3 w-3 text-[#868E96]" />
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#0A0A0A]">{label}</div>
        </div>
        <div className="font-display text-2xl lg:text-3xl font-black tracking-tighter mt-1.5 text-[#868E96]">
          —
        </div>
        <div className="text-[10px] text-[#868E96] mt-1">Pas de données N-1</div>
      </div>
    );
  }
  return (
    <div data-testid={testid} className={`p-5 lg:p-6 ${up ? "bg-[#ECFDF5]" : "bg-[#FEF2F2]"}`}>
      <div className="flex items-center gap-2">
        <Target className={`h-3 w-3 ${up ? "text-[#059669]" : "text-[#E60000]"}`} />
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#0A0A0A]">{label}</div>
      </div>
      <div className={`font-display text-2xl lg:text-3xl font-black tracking-tighter mt-1.5 flex items-center gap-2 ${up ? "text-[#059669]" : "text-[#E60000]"}`}>
        <Icon className="h-6 w-6" /> {value > 0 ? "+" : ""}{value.toFixed(1)}%
      </div>
    </div>
  );
}

function F({ label, value, onChange, type = "text", step, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">{label}</Label>
      <Input data-testid={testid} type={type} step={step} value={value} onChange={(e) => onChange(e.target.value)} className="h-11" />
    </div>
  );
}
